import React, { useState, useEffect } from 'react';
import {
  MantineProvider,
  Container,
  Group,
  ActionIcon,
  Center,
  Stack,
  Text,
  Space,
  createTheme,
  useMantineColorScheme,
  useComputedColorScheme,
  Tabs,
} from '@mantine/core';
import { IconSun, IconMoonStars, IconMail, IconBrandGithub, IconBrandLinkedin, IconCopy, IconCheck, IconCopyPlus, IconExternalLink } from '@tabler/icons-react';

const CompanyIcon = () => (
  <div
    style={{
      width: 120,
      height: 120,
      borderRadius: '50%',
      overflow: 'hidden',
      marginBottom: '2rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(255, 255, 255, 0.1)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
    }}
  >
    <img
      src="/logo.jpg"
      alt="ZUSE Logo"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
      }}
    />
  </div>
);

const CompanyDescription = () => (
  <Stack gap="md" align="center" style={{ maxWidth: 600, textAlign: 'center' }}>
    <Text size="md" c="dimmed">
      Placeholder website for ZUSE, an ICT company in Bruges.
    </Text>
  </Stack>
);

const CompanyDetails = () => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [isAnimating, setIsAnimating] = useState(false);
  
  const details = [
    { label: 'Company Name', value: 'ZUSE' },
    { label: 'Address', value: 'West-Gistelhof 20' },
    { label: 'City', value: '8000 Brugge' },
    { label: 'Country', value: 'Belgium' },
    { label: 'Email', value: 'info@zuse.be' },
    { label: 'Phone', value: '+32474187143' },
    { label: 'Business Number', value: 'BE1018698156' },
  ];

  const portfolioItems = [
    { label: 'Web Applications', value: 'React, Angular, Vue.js', thumbnail: 'https://picsum.photos/60/60?random=1' },
    { label: 'Backend Services', value: 'Java, Python, Node.js', thumbnail: 'https://picsum.photos/60/60?random=2' },
    { label: 'Mobile Development', value: 'React Native, Flutter', thumbnail: 'https://picsum.photos/60/60?random=3' },
    { label: 'AI Solutions', value: 'Machine Learning, NLP', thumbnail: 'https://picsum.photos/60/60?random=4' },
    { label: 'Database Design', value: 'PostgreSQL, MongoDB', thumbnail: 'https://picsum.photos/60/60?random=5' },
    { label: 'Cloud Platforms', value: 'AWS, Azure, GCP', thumbnail: 'https://picsum.photos/60/60?random=6' },
  ];

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyAllToClipboard = async () => {
    try {
      const currentData = activeTab === 'overview' ? details : portfolioItems;
      const allData = currentData.map(item => item.value).join('\n');
      await navigator.clipboard.writeText(allData);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) {
      console.error('Failed to copy all data: ', err);
    }
  };

  const handleTabChange = (value: string | null) => {
    if (value === activeTab) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveTab(value);
      setTimeout(() => setIsAnimating(false), 50);
    }, 150);
  };

  return (
    <div style={{ 
      background: 'var(--mantine-color-body)',
      borderRadius: '12px',
      border: '1px solid var(--mantine-color-default-border)',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      maxWidth: 600
    }}>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--mantine-color-default-border)',
            backgroundColor: 'var(--mantine-color-gray-light)',
          }}
        >
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="portfolio">Portfolio</Tabs.Tab>
          </Tabs.List>
          {activeTab === 'overview' && (
            <ActionIcon
              variant="subtle"
              color={copiedAll ? 'green' : 'gray'}
              onClick={copyAllToClipboard}
              title={copiedAll ? 'All data copied!' : 'Copy all data'}
            >
              {copiedAll ? <IconCheck size={16} /> : <IconCopyPlus size={16} />}
            </ActionIcon>
          )}
        </div>
        <Tabs.Panel value="overview">
          <div style={{
            opacity: isAnimating ? 0 : 1,
            transform: isAnimating ? 'translateY(10px)' : 'translateY(0)',
            transition: 'all 0.2s ease-in-out'
          }}>
          {details.map((detail, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: index < details.length - 1 ? '1px solid var(--mantine-color-default-border)' : 'none',
            transition: 'background-color 0.2s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-light-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onClick={() => copyToClipboard(detail.value, index)}
        >
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Text fw={600} size="sm" c="dimmed" style={{ width: '180px', textAlign: 'left' }}>
              {detail.label}
            </Text>
            <Text fw={500} size="md" style={{ flex: 1, textAlign: 'left' }}>
              {detail.value}
            </Text>
          </div>
          <ActionIcon
            variant="subtle"
            color={copiedIndex === index ? 'green' : 'gray'}
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(detail.value, index);
            }}
            title={copiedIndex === index ? 'Copied!' : 'Copy to clipboard'}
            style={{ marginLeft: '12px' }}
          >
            {copiedIndex === index ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </ActionIcon>
        </div>
      ))}
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="portfolio">
          <div style={{
            opacity: isAnimating ? 0 : 1,
            transform: isAnimating ? 'translateY(10px)' : 'translateY(0)',
            transition: 'all 0.2s ease-in-out'
          }}>
          {portfolioItems.map((item, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: index < portfolioItems.length - 1 ? '1px solid var(--mantine-color-default-border)' : 'none',
                transition: 'background-color 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-light-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={() => window.open('https://github.com/BrunoVandekerkhove', '_blank')}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <img
                  src={item.thumbnail}
                  alt={item.label}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                    marginRight: '16px',
                    border: '1px solid var(--mantine-color-default-border)'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="sm" c="dimmed" style={{ marginBottom: '4px' }}>
                    {item.label}
                  </Text>
                  <Text fw={500} size="md" c="dimmed">
                    {item.value}
                  </Text>
                </div>
              </div>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://github.com/BrunoVandekerkhove', '_blank');
                }}
                title="View on GitHub"
                style={{ marginLeft: '12px' }}
              >
                <IconExternalLink size={16} />
              </ActionIcon>
            </div>
          ))}
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

const SocialFooter = () => (
  <Group justify="center" gap="lg">
    <ActionIcon
      component="a"
      href="mailto:info@zuse.be"
      size="lg"
      variant="subtle"
    >
      <IconMail size={24} />
    </ActionIcon>
    <ActionIcon
      component="a"
      href="https://github.com/BrunoVandekerkhove"
      target="_blank"
      size="lg"
      variant="subtle"
    >
      <IconBrandGithub size={24} />
    </ActionIcon>
    <ActionIcon
      component="a"
      href="https://www.linkedin.com/in/bruno-v-507258a2/"
      target="_blank"
      size="lg"
      variant="subtle"
    >
      <IconBrandLinkedin size={24} />
    </ActionIcon>
  </Group>
);

const ThemeToggle = () => {
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');

  const getIcon = () => {
    return computedColorScheme === 'dark' ? <IconSun size={18} /> : <IconMoonStars size={18} />;
  };

  const getNextScheme = () => {
    // Toggle based on what's actually showing, not the setting
    return computedColorScheme === 'dark' ? 'light' : 'dark';
  };

  const getTitle = () => {
    return computedColorScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  };
  
  return (
    <ActionIcon
      variant="subtle"
      color="gray"
      onClick={() => setColorScheme(getNextScheme())}
      title={getTitle()}
    >
      {getIcon()}
    </ActionIcon>
  );
};

function App() {
  const theme = createTheme({
    colors: {
      dark: [
        '#C1C2C5',
        '#A6A7AB', 
        '#909296',
        '#5c5f66',
        '#373A40',
        '#2C2E33',
        '#25262b',
        '#1A1B1E',
        '#141517',
        '#0C0D0F',
      ],
      gray: [
        '#f8f9fa',
        '#f1f3f4',
        '#e9ecef',
        '#dee2e6',
        '#ced4da',
        '#adb5bd',
        '#6c757d',
        '#495057',
        '#343a40',
        '#212529',
      ],
    },
    white: '#ffffff',
    black: '#000000',
  });

  useEffect(() => {
    // Clear any stored color scheme preference to ensure auto mode works
    localStorage.removeItem('mantine-color-scheme');
    
    // Force light mode colors on mobile devices to prevent Opera dark mode override
    const style = document.createElement('style');
    style.textContent = `
      /* Opera-specific fixes */
      html, body {
        background-color: var(--mantine-color-body) !important;
        color-scheme: light dark !important;
      }
      
      [data-mantine-color-scheme="light"] {
        background-color: #ffffff !important;
        color: #000000 !important;
        -webkit-color-scheme: light !important;
        color-scheme: light !important;
      }
      
      [data-mantine-color-scheme="light"] .mantine-Container-root,
      [data-mantine-color-scheme="light"] .mantine-Stack-root,
      [data-mantine-color-scheme="light"] .mantine-Center-root {
        background-color: transparent !important;
        color: inherit !important;
      }
      
      [data-mantine-color-scheme="light"] .mantine-Container-root {
        background-color: #ffffff !important;
      }
      
      [data-mantine-color-scheme="light"] .mantine-Text-root {
        color: inherit !important;
      }
      
      [data-mantine-color-scheme="light"] [data-dimmed] {
        color: #6c757d !important;
      }
      
      /* Force background for Opera */
      @media (max-width: 768px) {
        [data-mantine-color-scheme="light"] {
          background: #ffffff !important;
          filter: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <Container size="md" py="xl">
        <Center>
          <Stack gap="xl" align="center">
            <Group justify="flex-end" style={{ width: '100%', position: 'absolute', top: 20, right: 20 }}>
              <ThemeToggle />
            </Group>
            <CompanyIcon />
            <CompanyDescription />
            <Space h="md" />
            <CompanyDetails />
            <Space h="lg" />
            <SocialFooter />
          </Stack>
        </Center>
      </Container>
    </MantineProvider>
  );
}

export default App;
